import clsx from 'clsx';
import dynamic from 'next/dynamic';

import Grid, { Container } from 'components/Grid';
import Typography from 'components/Typography';
import { useUserAuthDialog } from 'components/UserAuthDialog';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import { yearsSinceGetapp } from 'helpers/brandDates';
import { ROUTE_NAMES } from 'routes';
import routeService from 'services/routes';

import Link from './components/Link';
import SocialLink from './components/SocialLink';

import fifteenYearsLogo from 'assets/ga-15-plus.svg';

import classes from './Footer.module.scss';

const DialogInternational = dynamic(
  () => import('./components/DialogInternational')
);

/** @type {import("./types").default} */
const Footer = ({ className, isSignedIn }) => {
  const [UserAuthDialog, openUserAuthDialog] = useUserAuthDialog({
    origin: 'footer',
  });
  const { hreflangs = [], isCypressTakingSnapshots } = useAppContext();
  const { addEvent } = useEventContext({
    engagementComponent: 'footer',
  });
  const hasInternationalUrls = !!hreflangs.length;

  const onClick = (selectedVariant) => (event) => {
    event.preventDefault();
    openUserAuthDialog({ selectedVariant });
  };

  return (
    <footer className={clsx('Footer', className, classes.root)}>
      <UserAuthDialog />
      <Container className={classes.container}>
        <Grid container={true} className={classes.gridTop}>
          <Grid
            className={classes.countryAndSocial}
            item={true}
            container={true}
            md={4}
            lg={3}
          >
            {hasInternationalUrls && (
              <Grid
                item={true}
                md={12}
                sm={6}
                xs={12}
                data-testid="dialogInternational-grid"
              >
                <Typography
                  className={classes.sectionTitle}
                  variant="heading-sm"
                >
                  Select your country
                </Typography>
                <DialogInternational />
              </Grid>
            )}
            <Grid
              className={clsx(classes.socialLinks, {
                [classes.socialLinksSingle]: !hasInternationalUrls,
              })}
              item={true}
              md={12}
              sm={6}
              xs={12}
              data-testid="social-icons"
            >
              <SocialLink name="x-twitter" href="https://x.com/getapp" />
              <SocialLink
                name="facebook"
                icon="facebook-square"
                href="https://www.facebook.com/GetAppcom"
              />
              <SocialLink
                name="linkedin"
                href="https://www.linkedin.com/company/getapp-com/posts/"
              />
              <SocialLink
                name="instagram"
                href="https://www.instagram.com/getappcom/"
              />
            </Grid>
          </Grid>
          <Grid item={true} container={true} md={8} lg={9}>
            <Grid
              item={true}
              container={true}
              lg={6}
              className={classes.gridAddress}
            >
              <Grid item={true} sm={5} xs={12}>
                <Typography
                  className={classes.sectionTitle}
                  variant="heading-sm"
                >
                  GetApp
                </Typography>
                <Typography className={classes.sectionTitle} variant="body-sm">
                  100 S. Wacker Dr.
                  <br />
                  Ste. 600
                  <br />
                  Chicago, IL 60606
                  <br />
                  USA
                </Typography>
              </Grid>
            </Grid>
            <Grid item={true} container={true} lg={6}>
              <Grid className={classes.section} item={true} sm={4} xs={6}>
                <Typography
                  className={classes.sectionTitle}
                  variant="heading-sm"
                >
                  For you
                </Typography>
                <div className={classes.links}>
                  {!isSignedIn && (
                    <>
                      <Link
                        {...addEvent({
                          engagementDestination: 'go-to_join-getapp',
                          engagementDetails: 'text-link_join',
                          name: 'sign_up_cta',
                        })}
                        onClick={onClick('join')}
                        className={classes.primary}
                      >
                        Join
                      </Link>
                      <Link
                        onClick={onClick('signin')}
                        {...addEvent({
                          engagementDestination: 'sign-in',
                          engagementDetails: 'text-link_sign-in',
                          name: 'login_cta',
                        })}
                        href="https://www.getapp.com/#"
                      >
                        Sign in
                      </Link>
                    </>
                  )}
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_software-categories',
                      engagementDetails: 'text-link_categories',
                      name: 'engagement_navigation',
                    })}
                    href="/browse/"
                  >
                    Software categories
                  </Link>
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_resources',
                      engagementDetails: 'text-link_resources',
                      name: 'engagement_navigation',
                    })}
                    href={routeService.generatePath(ROUTE_NAMES.RESOURCES)}
                  >
                    Blog & research
                  </Link>
                </div>
              </Grid>
              <Grid className={classes.section} item={true} sm={4} xs={6}>
                <Typography
                  className={classes.sectionTitle}
                  variant="heading-sm"
                >
                  For vendors
                </Typography>
                <div className={classes.links}>
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_gdm',
                      engagementDetails: 'text-link_get-listed',
                      name: 'click',
                      clickUrl: 'https://www.g2.com/products/new',
                      outboundLinkType: 'general link',
                      outboundGeneralLinkClickCount: 1,
                    })}
                    className={classes.primary}
                    newWindow={true}
                    href="https://www.g2.com/products/new"
                  >
                    Get listed
                  </Link>
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_vendor-portal',
                      engagementDetails: 'text-link_your-account',
                      name: 'click',
                      clickUrl: 'https://app.g2digitalmarkets.com/login',
                      outboundLinkType: 'general link',
                      outboundGeneralLinkClickCount: 1,
                    })}
                    newWindow={true}
                    href="https://app.g2digitalmarkets.com/login"
                  >
                    Your account
                  </Link>
                </div>
              </Grid>
              <Grid className={classes.section} item={true} sm={4} xs={6}>
                <Typography
                  className={classes.sectionTitle}
                  variant="heading-sm"
                >
                  Our company
                </Typography>
                <div className={classes.links}>
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_about',
                      engagementDetails: 'text-link_about-us',
                      name: 'engagement_navigation',
                    })}
                    href={routeService.generatePath(ROUTE_NAMES.ABOUT)}
                  >
                    About us
                  </Link>
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_careers',
                      engagementDetails: 'text-link_careers',
                      name: 'click',
                      clickUrl: 'https://company.g2.com/careers',
                      outboundLinkType: 'general link',
                      outboundGeneralLinkClickCount: 1,
                    })}
                    newWindow={true}
                    href="https://company.g2.com/careers"
                  >
                    Careers
                  </Link>
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_press',
                      engagementDetails: 'text-link_press',
                      name: 'engagement_navigation',
                    })}
                    href="/press/"
                  >
                    News
                  </Link>
                  <Link
                    {...addEvent({
                      engagementDestination: 'go-to_terms-of-use',
                      engagementDetails: 'text-link_legal',
                      name: 'engagement_navigation',
                    })}
                    href="https://legal.g2.com/"
                  >
                    Legal Terms
                  </Link>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid className={classes.gridBottom} container={true}>
          <Grid item={true} md={5} xs={12}>
            <div className={classes.gaLogo}>
              <Link
                {...addEvent({
                  engagementDestination: 'go-to_home',
                  engagementDetails: 'logo_getapp',
                  name: 'engagement_navigation',
                })}
                href={routeService.generatePath(ROUTE_NAMES.HOME)}
              >
                <img
                  src={fifteenYearsLogo.src}
                  alt={`${yearsSinceGetapp}+ YEARS RECOMMENDING SOFTWARE`}
                  width="373"
                  height="48"
                  {...(isCypressTakingSnapshots ? {} : { loading: 'lazy' })}
                />
              </Link>
            </div>
          </Grid>
          <Grid item={true} md={7} xs={12}>
            <Typography variant="tight-xxs">
              {`© 2010-${new Date().getFullYear()} GetApp. All Rights Reserved.`}
              <br />
              GetApp is a registered trademark of Nubera eBusiness S.L. Nubera
              eBusiness uses its own and third-party cookies. By using the
              website you are accepting the use of these cookies. To get more
              information about our cookies{' '}
              <a
                {...addEvent({
                  engagementDestination: 'go-to_privacy-policy',
                  engagementDetails: 'text-link_click-here',
                  name: 'engagement_navigation',
                })}
                className={classes.primary}
                href="/cookie_settings/"
              >
                click here
              </a>
              .
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </footer>
  );
};

export default Footer;
